package utils

/**
 * Month arithmetic the dashboards share.
 *
 * Every surface — web, iOS, Android — defaults to the last COMPLETE month rather than
 * the in-progress current one: mid-month the fixed bills have landed but the income
 * hasn't, so "this month" reads as a wildly over-spent month. The user can still step
 * to the current month, which is badged as in progress.
 *
 * Keeping the math here means the December→January rollover and the stepper's two
 * end-stops are implemented and tested once instead of once per platform.
 *
 * Months are 1-based throughout. "Now" is passed in rather than read from a clock so
 * the behaviour is deterministic and testable, and because commonMain has no shared
 * date API here.
 */
object DashboardMonth {

    /**
     * Step ([y0], [m0]) by [delta] whole months and clamp the result to the inclusive
     * window [minY]/[minM]‥[maxY]/[maxM].
     */
    fun stepClamped(
        y0: Int,
        m0: Int,
        delta: Int,
        minY: Int,
        minM: Int,
        maxY: Int,
        maxM: Int,
    ): Pair<Int, Int> {
        val total = (y0 * 12 + (m0 - 1) + delta)
            .coerceIn(minY * 12 + (minM - 1), maxY * 12 + (maxM - 1))
        return (total / 12) to (total % 12 + 1)
    }

    /**
     * The last complete month relative to ([nowYear], [nowMonth]).
     *
     * In January this rolls back to December of the *prior* year. Returned as a pair
     * so a caller can't accidentally pair the month with the current year.
     */
    fun lastComplete(nowYear: Int, nowMonth: Int): Pair<Int, Int> =
        if (nowMonth == 1) (nowYear - 1) to 12 else nowYear to (nowMonth - 1)

    /** True when ([year], [month]) is the current, still-incomplete calendar month. */
    fun isInProgress(year: Int, month: Int, nowYear: Int, nowMonth: Int): Boolean =
        year == nowYear && month == nowMonth
}
